import { ReactNode } from 'react';

import { Button } from '@/components/core/Button';
import { formatBytes } from '@/components/core/Uploader/utils';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useFormatAttributeValue } from '@/components/datamodel/utils/formatAttributeValue';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetApiRecord } from '@/pages/dam/api/assets.types';
import { fileFormatOf } from '@/pages/dam/utils/damFileFormat';
import { DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

const EMPTY_VALUE = '–';

/** Wireframe labels of system values whose data-model label is generic ("Created At"). */
const SYSTEM_LABEL_KEYS: Record<string, string> = {
    updatedAt__: 'DAM.DETAIL.FIELD.EDITED',
    createdAt__: 'DAM.DETAIL.FIELD.UPLOAD_DATE',
    mimeType__: 'DAM.DETAIL.FIELD.FORMAT',
    width__: 'DAM.DETAIL.FIELD.RESOLUTION',
    fileSize__: 'DAM.DETAIL.FIELD.FILE_SIZE',
    linkedItemCount__: 'DAM.DETAIL.FIELD.LINKED_ARTICLES',
};

interface DamDetailRow {
    name: string;
    label: string;
    value: ReactNode;
}

interface DamDetailRowActions {
    onShowLinkedArticles: () => void;
    onShowHistory: () => void;
}

const isBlank = (value: unknown) =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/** The API sends system timestamps as epoch milliseconds; the formatter parses dates and ISO strings. */
const asDate = (value: unknown) => (typeof value === 'number' ? new Date(value) : value);

const InlineLink = ({ children, onClick }: { children: ReactNode; onClick: () => void }) => (
    <Button variant="ghost" onClick={onClick} className="tw-h-auto tw-p-0 tw-font-bold tw-underline">
        {children}
    </Button>
);

/**
 * Read rows of "Asset data" from the `ASSET_DATA` attributes: system values render as in the
 * wireframe (format, resolution, file size, linked articles with "Show", edit date with
 * "History"), every other attribute through the data-model formatter.
 */
function useDamDetailRows(asset: AssetApiRecord, attributes: DataModelAttribute[], actions: DamDetailRowActions) {
    const { t } = useTranslation();
    const { formatValue } = useFormatAttributeValue();
    const { translateAttributeLabel } = useAttributeLabelTranslator();

    const labelOf = (attribute: DataModelAttribute) =>
        SYSTEM_LABEL_KEYS[attribute.name] ? t(SYSTEM_LABEL_KEYS[attribute.name]) : translateAttributeLabel(attribute);

    const format = (attribute: DataModelAttribute, value: unknown): string => {
        if (isBlank(value)) {
            return EMPTY_VALUE;
        }
        const display = formatValue(attribute, value);
        return display && display !== '-' ? display : EMPTY_VALUE;
    };

    const valueOf = (attribute: DataModelAttribute): ReactNode => {
        const value = asset[attribute.name];
        switch (attribute.name) {
            case 'filename__':
                return isBlank(value) ? EMPTY_VALUE : String(value);
            case 'mimeType__':
                return fileFormatOf(asset.filename__, asset.mimeType__) || EMPTY_VALUE;
            case 'width__':
                return asset.width__ && asset.height__ ? `${asset.width__} × ${asset.height__} px` : EMPTY_VALUE;
            case 'fileSize__':
                return asset.fileSize__ ? formatBytes(asset.fileSize__) : EMPTY_VALUE;
            case 'createdAt__':
                return format({ ...attribute, typeName: DataModelAttributeTypeName.Date }, asDate(value));
            case 'linkedItemCount__': {
                const count = Number(value ?? 0);
                if (count === 0) {
                    return t('DAM.CARD.LINKED_PRODUCTS_NONE');
                }
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                        {count === 1 ? t('DAM.DETAIL.ARTICLE_COUNT_ONE') : t('DAM.DETAIL.ARTICLE_COUNT', { count: count.toLocaleString() })}
                        <InlineLink onClick={actions.onShowLinkedArticles}>{t('DAM.DETAIL.SHOW')}</InlineLink>
                    </span>
                );
            }
            case 'updatedAt__':
                return (
                    <span className="tw-inline-flex tw-items-center tw-gap-1">
                        {format(attribute, asDate(value))}
                        <span aria-hidden>•</span>
                        <InlineLink onClick={actions.onShowHistory}>{t('DAM.DETAIL.HISTORY')}</InlineLink>
                    </span>
                );
            default:
                return format(attribute, value);
        }
    };

    return attributes.map((attribute): DamDetailRow => ({ name: attribute.name, label: labelOf(attribute), value: valueOf(attribute) }));
}

export { EMPTY_VALUE, useDamDetailRows };
export type { DamDetailRow, DamDetailRowActions };
